import { Component } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Button,
    Dialog,
    Heading,
    IconButton,
    Pane,
    RefreshIcon,
    SearchInput,
    Spinner,
    Table,
    Text,
    toaster,
    Tab,
    Tablist,
    EyeOpenIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    AddIcon
} from 'evergreen-ui';

import { getCatalog, createProductFromCatalog } from '../actions/CatalogActions';
import HeoSyncModal from '../components/HeoSyncModal';
import CatalogDetailModal from '../components/CatalogDetailModal';
import Config from '../../../util/Configuration';
import Cookie from 'react-cookies';

const CATALOG_IMAGE_BUCKET = process.env.REACT_APP_CATALOG_IMAGE_BUCKET || 'https://krit-content.ams3.cdn.digitaloceanspaces.com/catalog-images';
const PLACEHOLDER_IMAGE = '/images/placeholder-product.png';

// JSON Tree View Component
class JsonTreeNode extends Component {
    constructor(props) {
        super(props);
        this.state = { expanded: props.defaultExpanded || false };
    }

    toggle = () => {
        this.setState({ expanded: !this.state.expanded });
    };

    renderValue(value, key) {
        if (value === null) {
            return <Text color="muted" fontStyle="italic">null</Text>;
        }
        if (value === undefined) {
            return <Text color="muted" fontStyle="italic">undefined</Text>;
        }
        if (typeof value === 'boolean') {
            return <Text color={value ? 'green' : 'red'}>{value.toString()}</Text>;
        }
        if (typeof value === 'number') {
            return <Text color="blue">{value}</Text>;
        }
        if (typeof value === 'string') {
            if (value.length > 100) {
                return <Text color="orange" title={value}>"{value.substring(0, 100)}..."</Text>;
            }
            return <Text color="orange">"{value}"</Text>;
        }
        if (Array.isArray(value)) {
            return <JsonTreeNode nodeKey={key} value={value} isArray defaultExpanded={false} />;
        }
        if (typeof value === 'object') {
            return <JsonTreeNode nodeKey={key} value={value} defaultExpanded={false} />;
        }
        return <Text>{String(value)}</Text>;
    }

    render() {
        const { nodeKey, value, isArray } = this.props;
        const { expanded } = this.state;

        const isExpandable = (typeof value === 'object' && value !== null);
        const entries = isExpandable ? Object.entries(value) : [];
        const isEmpty = entries.length === 0;

        if (!isExpandable) {
            return this.renderValue(value, nodeKey);
        }

        const bracketOpen = isArray ? '[' : '{';
        const bracketClose = isArray ? ']' : '}';

        return (
            <Pane>
                <Pane 
                    display="flex" 
                    alignItems="center" 
                    cursor={isEmpty ? 'default' : 'pointer'}
                    onClick={isEmpty ? undefined : this.toggle}
                    style={{ userSelect: 'none' }}
                >
                    {!isEmpty && (
                        <ChevronRightIcon 
                            size={12} 
                            marginRight={4}
                            style={{ 
                                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.1s'
                            }}
                        />
                    )}
                    {isEmpty && <Pane width={16} />}
                    <Text color="muted" fontSize={12}>
                        {bracketOpen}
                        {!expanded && !isEmpty && <Text color="muted">...</Text>}
                        {(isEmpty || !expanded) && bracketClose}
                        {!isEmpty && <Text color="muted" marginLeft={4} fontSize={11}>({entries.length} {isArray ? 'items' : 'keys'})</Text>}
                    </Text>
                </Pane>
                {expanded && !isEmpty && (
                    <Pane marginLeft={16} borderLeft="1px solid #E4E7EB" paddingLeft={8}>
                        {entries.map(([key, val], idx) => (
                            <Pane key={key} display="flex" alignItems="flex-start" marginY={2}>
                                <Text fontWeight={500} color="purple" marginRight={4} fontSize={12}>
                                    {isArray ? `[${key}]` : key}:
                                </Text>
                                {this.renderValue(val, key)}
                            </Pane>
                        ))}
                        <Text color="muted" fontSize={12}>{bracketClose}</Text>
                    </Pane>
                )}
            </Pane>
        );
    }
}

let searchTimeout = null;

class ListCatalog extends Component {
    constructor(props) {
        super(props);

        // Parse page from URL query params
        const urlParams = new URLSearchParams(props.location?.search || '');
        const urlPage = parseInt(urlParams.get('page')) || 1;

        this.state = {
            page: urlPage - 1, // Internal state is 0-indexed
            pages: 0,
            count: 0,
            search: urlParams.get('search') || '',
            syncModalOpen: false,
            typeFilter: urlParams.get('type') || '',
            sourceFilter: urlParams.get('source') || '',
            categoryFilter: urlParams.get('category') || '',
            categories: [],
            imageModalOpen: false,
            imageModalUrl: null,
            imageModalName: '',
            syncingItemId: null,
            // Detail modal state
            detailModalOpen: false,
            detailItem: null,
            detailTab: 0,
            detailImageIndex: 0,
            refreshingDetailItem: false
        };

        this.itemsPerPage = 20;
    }

    componentDidMount() {
        this.loadCatalog();
        this.loadCategories();
    }

    loadCategories = async () => {
        try {
            const response = await fetch(`${Config.host}/v2/catalog/categories`, {
                headers: {
                    'session': Cookie.load('nax-session')
                }
            });
            const data = await response.json();
            if (data.categories) {
                this.setState({ categories: data.categories });
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    componentDidUpdate(prevProps) {
        if (this.props.catalog.count !== this.state.count) {
            this.setState({
                count: this.props.catalog.count,
                pages: Math.ceil(this.props.catalog.count / this.itemsPerPage)
            });
        }
    }

    updateUrl = () => {
        const params = new URLSearchParams();
        if (this.state.page > 0) params.set('page', this.state.page + 1);
        if (this.state.search) params.set('search', this.state.search);
        if (this.state.typeFilter) params.set('type', this.state.typeFilter);
        if (this.state.categoryFilter) params.set('category', this.state.categoryFilter);
        if (this.state.sourceFilter) params.set('source', this.state.sourceFilter);
        
        const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname;
        window.history.replaceState(null, '', newUrl);
    };

    loadCatalog = (opts = {}) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
            const params = {
                start: opts.page !== undefined ? opts.page * this.itemsPerPage : this.state.page * this.itemsPerPage,
                limit: this.itemsPerPage,
                search: opts.search !== undefined ? opts.search : this.state.search
            };

            const extras = {};
            if (this.state.typeFilter) extras.type = this.state.typeFilter;
            if (this.state.sourceFilter) extras.source = this.state.sourceFilter;
            if (this.state.categoryFilter) extras.category = this.state.categoryFilter;

            this.props.dispatch(getCatalog(params, extras));
            this.updateUrl();
        }, 300);
    };

    handleSearchChange = (e) => {
        const search = e.target.value;
        this.setState({ search, page: 0 }, () => {
            this.loadCatalog({ search, page: 0 });
        });
    };

    handleCategoryFilter = (e) => {
        this.setState({ categoryFilter: e.target.value, page: 0 }, () => {
            this.loadCatalog({ page: 0 });
        });
    };

    handlePageChange = (page) => {
        this.setState({ page }, () => {
            this.loadCatalog({ page });
        });
    };

    handlePageSelectChange = (e) => {
        const page = parseInt(e.target.value);
        this.handlePageChange(page);
    };

    handleTypeFilter = (e) => {
        this.setState({ typeFilter: e.target.value, page: 0 }, () => {
            this.loadCatalog({ page: 0 });
        });
    };

    handleSourceFilter = (e) => {
        this.setState({ sourceFilter: e.target.value, page: 0 }, () => {
            this.loadCatalog({ page: 0 });
        });
    };

    openSyncModal = () => {
        this.setState({ syncModalOpen: true });
    };

    openImageModal = (thumbnail, name) => {
        const fullImageUrl = `${CATALOG_IMAGE_BUCKET}/original_${thumbnail}.jpg`;
        this.setState({ 
            imageModalOpen: true, 
            imageModalUrl: fullImageUrl,
            imageModalName: name
        });
    };

    closeImageModal = () => {
        this.setState({ 
            imageModalOpen: false, 
            imageModalUrl: null,
            imageModalName: ''
        });
    };

    openDetailModal = (item) => {
        this.setState({
            detailModalOpen: true,
            detailItem: item,
            detailTab: 0,
            detailImageIndex: 0
        });
    };

    closeDetailModal = () => {
        this.setState({
            detailModalOpen: false,
            detailItem: null,
            detailTab: 0,
            detailImageIndex: 0,
            refreshingDetailItem: false
        });
    };

    refreshDetailItem = async () => {
        if (!this.state.detailItem?.id) return;
        
        this.setState({ refreshingDetailItem: true });
        
        try {
            const response = await fetch(`${Config.host}/v2/catalog/${this.state.detailItem.id}`, {
                headers: {
                    'session': Cookie.load('nax-session')
                }
            });
            const data = await response.json();
            
            if (data && data.id) {
                this.setState({ 
                    detailItem: data,
                    refreshingDetailItem: false 
                });
                toaster.success('Date actualizate');
            } else {
                this.setState({ refreshingDetailItem: false });
                toaster.danger('Nu s-au putut actualiza datele');
            }
        } catch (error) {
            console.error('Error refreshing catalog item:', error);
            this.setState({ refreshingDetailItem: false });
            toaster.danger('Eroare la actualizarea datelor');
        }
    };

    hasUnsyncedImages = (item) => {
        if (!item.images || item.images.length === 0) return false;
        return item.images.some(img => img.synced === 0);
    };

    getSyncedImages = (item) => {
        if (!item.images || item.images.length === 0) return [];
        return item.images.filter(img => img.synced === 1);
    };

    syncItemImages = async (itemId) => {
        this.setState({ syncingItemId: itemId });
        
        try {
            const response = await fetch(`${Config.host}/v2/catalog/${itemId}/sync-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'session': Cookie.load('nax-session')
                }
            });
            
            const result = await response.json();
            
            if (response.ok) {
                toaster.success(result.message || 'Imagini sincronizate cu succes');
                this.loadCatalog();
            } else {
                toaster.danger(result.error || 'Eroare la sincronizare');
            }
        } catch (error) {
            toaster.danger('Eroare la sincronizare: ' + error.message);
        }
        
        this.setState({ syncingItemId: null });
    };

    handleCreateProduct = () => {
        if (this.state.detailItem) {
            this.props.dispatch(createProductFromCatalog(this.state.detailItem.id));
            this.closeDetailModal();
        }
    };

    syncDetailItemImages = async () => {
        if (!this.state.detailItem?.id) return;
        
        const itemId = this.state.detailItem.id;
        this.setState({ syncingItemId: itemId });
        
        try {
            const response = await fetch(`${Config.host}/v2/catalog/${itemId}/sync-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'session': Cookie.load('nax-session')
                }
            });
            
            const result = await response.json();
            
            if (response.ok) {
                toaster.success(result.message || 'Imagini sincronizate cu succes');
                // Refresh the detail item to show newly synced images
                await this.refreshDetailItem();
                // Also refresh the list
                this.loadCatalog();
            } else {
                toaster.danger(result.error || 'Eroare la sincronizare');
            }
        } catch (error) {
            toaster.danger('Eroare la sincronizare: ' + error.message);
        }
        
        this.setState({ syncingItemId: null });
    };

    closeSyncModal = () => {
        this.setState({ syncModalOpen: false });
        this.loadCatalog();
    };

    getTypeBadgeColor = (type) => {
        switch (type) {
            case 'product': return 'blue';
            case 'tcg_single': return 'purple';
            case 'service': return 'green';
            default: return 'neutral';
        }
    };

    getSourceBadgeColor = (source) => {
        switch (source) {
            case 'heo': return 'orange';
            case 'manual': return 'teal';
            default: return 'neutral';
        }
    };

    render() {
        const { catalog } = this.props;

        return (
            <Pane>
                <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={20}>
                    <Heading size={700}>Catalog</Heading>
                    <Button
                        appearance="primary"
                        iconBefore={RefreshIcon}
                        onClick={this.openSyncModal}
                    >
                        Sincronizează HEO
                    </Button>
                </Pane>

                <Pane display="flex" gap={10} marginBottom={20} flexWrap="wrap">
                    <SearchInput
                        placeholder="Caută în catalog..."
                        value={this.state.search}
                        onChange={this.handleSearchChange}
                        width={300}
                    />
                    <select
                        value={this.state.typeFilter}
                        onChange={this.handleTypeFilter}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        <option value="">Toate tipurile</option>
                        <option value="product">Product</option>
                        <option value="tcg_single">TCG Single</option>
                        <option value="service">Service</option>
                    </select>
                    <select
                        value={this.state.sourceFilter}
                        onChange={this.handleSourceFilter}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                        <option value="">Toate sursele</option>
                        <option value="manual">Manual</option>
                        <option value="heo">HEO</option>
                    </select>
                    <select
                        value={this.state.categoryFilter}
                        onChange={this.handleCategoryFilter}
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', maxWidth: '250px' }}
                    >
                        <option value="">Toate categoriile</option>
                        {this.state.categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </Pane>

                <Pane marginBottom={10}>
                    <Text color="muted">
                        Total: {catalog.count} articole
                    </Text>
                </Pane>

                <Table>
                    <Table.Head>
                        <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>
                            Imagine
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={150} flexShrink={0} flexGrow={0}>
                            Item ID
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell>Nume</Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
                            Tip
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
                            Sursă
                        </Table.TextHeaderCell>
                        <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
                            Acțiuni
                        </Table.TextHeaderCell>
                    </Table.Head>
                    <Table.Body>
                        {catalog.data && catalog.data.map((item) => (
                            <Table.Row key={item.id}>
                                <Table.Cell flexBasis={80} flexShrink={0} flexGrow={0}>
                                    {item.thumbnail && item.thumbnail !== PLACEHOLDER_IMAGE ? (
                                        <img
                                            src={`${CATALOG_IMAGE_BUCKET}/thumb_${item.thumbnail}.jpg`}
                                            alt={item.name}
                                            style={{
                                                width: 50,
                                                height: 50,
                                                objectFit: 'cover',
                                                borderRadius: 4,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => this.openImageModal(item.thumbnail, item.name)}
                                        />
                                    ) : (
                                        <Pane
                                            width={50}
                                            height={50}
                                            backgroundColor="#f5f5f5"
                                            borderRadius={4}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Text color="muted" size={300}>N/A</Text>
                                        </Pane>
                                    )}
                                </Table.Cell>
                                <Table.TextCell flexBasis={150} flexShrink={0} flexGrow={0}>
                                    {item.itemId || '-'}
                                </Table.TextCell>
                                <Table.TextCell>{item.name}</Table.TextCell>
                                <Table.Cell flexBasis={100} flexShrink={0} flexGrow={0}>
                                    <Badge color={this.getTypeBadgeColor(item.type)}>
                                        {item.type}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell flexBasis={100} flexShrink={0} flexGrow={0}>
                                    <Badge color={this.getSourceBadgeColor(item.source)}>
                                        {item.source}
                                    </Badge>
                                </Table.Cell>
                                <Table.Cell flexBasis={100} flexShrink={0} flexGrow={0}>
                                    <Pane display="flex" gap={4}>
                                        <IconButton
                                            icon={EyeOpenIcon}
                                            appearance="minimal"
                                            intent="none"
                                            onClick={() => this.openDetailModal(item)}
                                            title="Vezi detalii"
                                        />
                                        {this.hasUnsyncedImages(item) && (
                                            <IconButton
                                                icon={RefreshIcon}
                                                appearance="minimal"
                                                intent="none"
                                                disabled={this.state.syncingItemId === item.id}
                                                isLoading={this.state.syncingItemId === item.id}
                                                onClick={() => this.syncItemImages(item.id)}
                                                title="Sincronizează imaginile"
                                            />
                                        )}
                                    </Pane>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>

                {this.state.pages > 1 && (
                    <Pane display="flex" justifyContent="center" alignItems="center" marginTop={20} gap={10}>
                        <Button
                            disabled={this.state.page === 0}
                            onClick={() => this.handlePageChange(0)}
                        >
                            Prima
                        </Button>
                        <Button
                            disabled={this.state.page === 0}
                            onClick={() => this.handlePageChange(this.state.page - 1)}
                        >
                            Anterior
                        </Button>
                        <Pane display="flex" alignItems="center" gap={5}>
                            <Text>Pagina</Text>
                            <select
                                value={this.state.page}
                                onChange={this.handlePageSelectChange}
                                style={{ 
                                    padding: '8px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #ccc',
                                    maxHeight: '200px'
                                }}
                            >
                                {Array.from({ length: this.state.pages }, (_, i) => (
                                    <option key={i} value={i}>{i + 1}</option>
                                ))}
                            </select>
                            <Text>din {this.state.pages}</Text>
                        </Pane>
                        <Button
                            disabled={this.state.page >= this.state.pages - 1}
                            onClick={() => this.handlePageChange(this.state.page + 1)}
                        >
                            Următor
                        </Button>
                        <Button
                            disabled={this.state.page >= this.state.pages - 1}
                            onClick={() => this.handlePageChange(this.state.pages - 1)}
                        >
                            Ultima
                        </Button>
                    </Pane>
                )}

                <HeoSyncModal
                    isShown={this.state.syncModalOpen}
                    onClose={this.closeSyncModal}
                />

                {/* Image Preview Modal */}
                <Dialog
                    isShown={this.state.imageModalOpen}
                    title={this.state.imageModalName}
                    onCloseComplete={this.closeImageModal}
                    hasFooter={false}
                    width={800}
                >
                    <Pane display="flex" justifyContent="center" alignItems="center">
                        <img
                            src={this.state.imageModalUrl}
                            alt={this.state.imageModalName}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain'
                            }}
                        />
                    </Pane>
                </Dialog>

                {/* Detail Modal */}
                <CatalogDetailModal
                    isShown={this.state.detailModalOpen}
                    item={this.state.detailItem}
                    onClose={this.closeDetailModal}
                    onCreateProduct={this.handleCreateProduct}
                    showCreateButton={true}
                    onRefresh={this.refreshDetailItem}
                    refreshing={this.state.refreshingDetailItem}
                    onSyncImages={this.syncDetailItemImages}
                    syncing={this.state.syncingItemId === this.state.detailItem?.id}
                />
            </Pane>
        );
    }
}

function mapStateToProps(state) {
    return {
        catalog: state.catalog,
        location: state.router?.location
    };
}

export default connect(mapStateToProps)(ListCatalog);
